import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SearchableSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder?: string;
  className?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ value, onChange, options, placeholder = 'Select...', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ top: 0, left: 0, width: 200 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setHighlightIdx(0); }, [search]);

  const [openUp, setOpenUp] = useState(false);
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const reposition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldOpenUp = spaceBelow < 200 && rect.top > 200;
      setOpenUp(shouldOpenUp);
      // Cap the width to the viewport and keep the panel on-screen on narrow displays.
      const width = Math.min(Math.max(rect.width, 200), window.innerWidth - 16);
      setPos({
        top: shouldOpenUp ? undefined : rect.bottom + 3,
        bottom: shouldOpenUp ? (window.innerHeight - rect.top + 3) : undefined,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        width
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const toggleOption = (opt: string) => {
    if (value.includes(opt)) {
      onChange(value.filter(v => v !== opt));
    } else {
      onChange([...value, opt]);
    }
    setSearch('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key !== 'Tab' && e.key !== 'Shift') setIsOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightIdx]) toggleOption(filtered[highlightIdx]);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Backspace' && !search && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const displayText = value.length === 0 ? '' : value.length <= 2 ? value.join(', ') : `${value[0]}, +${value.length - 1}`;

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => { setIsOpen(true); inputRef.current?.focus(); }}
        className={className || "px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 w-48 cursor-text flex items-center gap-1 flex-wrap min-h-[36px]"}
      >
        {value.map(v => (
          <span key={v} className="inline-flex items-center gap-0.5 bg-blue-50 text-blue-700 text-xs font-medium px-1.5 py-0.5 rounded-md max-w-[120px]">
            <span className="truncate">{v}</span>
            <button type="button" onClick={e => { e.stopPropagation(); onChange(value.filter(x => x !== v)); }}
              className="text-blue-400 hover:text-blue-600 flex-shrink-0">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[60px] bg-transparent border-0 outline-none text-sm p-0"
        />
        {value.length > 0 && (
          <button type="button" onClick={e => { e.stopPropagation(); onChange([]); }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-auto">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        )}
      </div>
      {isOpen && createPortal(
        <div ref={dropdownRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto py-0.5"
          style={{ ...(openUp ? { bottom: pos.bottom } : { top: pos.top }), left: pos.left, width: pos.width }}>
          {filtered.length > 0 ? filtered.map((opt, idx) => {
            const selected = value.includes(opt);
            return (
              <button key={opt} type="button" tabIndex={-1}
                onClick={() => toggleOption(opt)}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${idx === highlightIdx ? 'bg-blue-50' : 'hover:bg-gray-50'} ${selected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                  {selected && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </span>
                {opt}
              </button>
            );
          }) : (
            <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
